import { Box, Grid, Skeleton, Typography } from "@mui/material";
import type { SpecimenAuditAnalyticsResponse } from "../../../services/api";
import { colors } from "../../../theme/colors";

type Props = {
  analytics?: SpecimenAuditAnalyticsResponse;
  isLoading: boolean;
  isError: boolean;
};

const Tile = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent: string;
}) => (
  <Box
    sx={{
      position: "relative",
      height: "100%",
      border: `1px solid ${colors.grey}`,
      borderRadius: "8px",
      backgroundColor: "#ffffff",
      p: 2.5,
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        backgroundColor: accent,
      }}
    />
    <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1.1, color: colors.lighterBlack }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 13, color: colors.grey, mt: 0.5 }}>
      {label}
    </Typography>
  </Box>
);

const KpiStrip = ({ analytics, isLoading, isError }: Props) => {
  if (isError) {
    return (
      <Typography sx={{ color: colors.red }}>Failed to load KPIs.</Typography>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Grid item xs={6} sm={4} md={2.4} key={i}>
            <Skeleton
              variant="rounded"
              height={104}
              sx={{ borderRadius: "8px" }}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  const tiles = [
    {
      label: "Status changes",
      value: analytics?.total_status_changes ?? 0,
      accent: colors.primary,
    },
    {
      label: "Hidden recalculations",
      value: analytics?.hidden_recalculation_count ?? 0,
      accent: colors.darkModalBackground,
    },
    {
      label: "Discrepancy rate",
      value:
        analytics != null
          ? `${(analytics.discrepancy_rate * 100).toFixed(1)}%`
          : "0.0%",
      accent: colors.red,
    },
    {
      label: "Avg lab handoff (min)",
      value:
        analytics?.avg_lab_handoff_minutes != null
          ? analytics.avg_lab_handoff_minutes.toFixed(1)
          : "—",
      accent: colors.darkGreen,
    },
    {
      label: "No-specimen events",
      value: analytics?.no_specimen_event_count ?? 0,
      accent: colors.grey,
    },
  ];

  return (
    <Grid container spacing={2}>
      {tiles.map((tile) => (
        <Grid item xs={6} sm={4} md={2.4} key={tile.label}>
          <Tile label={tile.label} value={tile.value} accent={tile.accent} />
        </Grid>
      ))}
    </Grid>
  );
};

export default KpiStrip;
